// Section wrapper for the Variation register inside the combined
// Variations & SOWs tab. Mirrors SowSection — section header (title +
// subtitle + primary `+ Add variation` button), include-archived
// toggle, table body slot, footer summary.
//
// Per brand SKILL.md §9.6: each section has its own header and primary
// `+ Add` button; the shared summary strip lives above on the tab,
// not here.

import { Plus } from "lucide-react";
import Button from "../../../common/Button";
import type { ReactNode } from "react";

interface VariationSectionProps {
  totalCount: number;
  visibleCount: number;
  includeArchived: boolean;
  onToggleArchived: (next: boolean) => void;
  onAdd: () => void;
  /** Filter affordance row. When supplied, replaces the default
   *  include-archived toggle entirely. */
  filterBar?: ReactNode;
  /** Foot-of-section content rendered below the table. When supplied,
   *  replaces the auto "Showing X of Y variations" count line — caller
   *  is expected to pass a TrackingPagination control that already
   *  carries its own count caption. */
  footer?: ReactNode;
  /** The section body (table + inline expands). */
  children: ReactNode;
}

export default function VariationSection({
  totalCount,
  visibleCount,
  includeArchived,
  onToggleArchived,
  onAdd,
  filterBar,
  footer,
  children,
}: VariationSectionProps) {
  return (
    <section
      aria-labelledby="variation-section-heading"
      className="space-y-3"
      data-testid="variation-section"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            id="variation-section-heading"
            className="text-h3 text-slate-900 dark:text-slate-50"
          >
            Variations
          </h2>
          <p className="text-body-sm text-slate-500 dark:text-slate-400 mt-1">
            Formal changes to the contract or a specific SOW. Approved
            variations contribute to the total contract value.
          </p>
        </div>
        <Button
          icon={<Plus className="h-4 w-4" />}
          onClick={onAdd}
          data-testid="variation-section-add"
        >
          Add variation
        </Button>
      </div>

      {filterBar ? (
        filterBar
      ) : (
        <div className="flex items-center">
          <label className="inline-flex items-center gap-2 text-body-sm text-slate-700 dark:text-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => onToggleArchived(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400 dark:border-slate-600 dark:bg-slate-800"
              data-testid="variation-section-include-archived"
            />
            Include archived
          </label>
        </div>
      )}

      {children}

      {footer ?? (
        <div
          className="flex justify-end text-caption text-slate-500 dark:text-slate-400"
          data-testid="variation-section-footer"
        >
          Showing {visibleCount} of {totalCount} variation
          {totalCount === 1 ? "" : "s"}
        </div>
      )}
    </section>
  );
}
